import { Link } from 'react-router-dom';
import { useResource } from '../lib/useResource.js';
import { formatDateTime } from '../lib/format.js';
import { Badge, Card, Empty, ErrorBox, PageHeader, Alert } from '../components/ui.js';
import { useSession } from '../lib/session.js';

interface Item {
  id: string;
  title: string;
  subtitle: string;
  at: string | null;
  href: string;
  tone: 'neutral' | 'attention' | 'critical';
}
interface Indicator {
  key: string;
  label: string;
  value: number;
  note: string;
}
interface OverviewData {
  isDemo: boolean;
  storeInfoConfirmed: boolean;
  needsHumanAttention: Item[];
  canReturn: Item[];
  scheduledServices: Item[];
  scheduledReminderSteps: Item[];
  indicators: Indicator[];
  notifications: { id: string; severity: string; title: string; body: string | null; createdAt: string }[];
}

function Lista({ items, empty }: { items: Item[]; empty: string }) {
  if (items.length === 0) return <Empty title={empty} />;
  return (
    <ul className="list">
      {items.map((item) => (
        <li key={item.id}>
          <div className="list-main">
            <div className="list-title">{item.title}</div>
            <div className="list-sub">{item.subtitle}</div>
          </div>
          {item.tone !== 'neutral' ? (
            <Badge tone={item.tone}>{item.tone === 'critical' ? 'urgente' : 'revisar'}</Badge>
          ) : null}
          <span className="list-when">{formatDateTime(item.at)}</span>
          <Link className="button small" to={item.href}>
            Abrir
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function Overview() {
  const { store } = useSession();
  const { data, error } = useResource<OverviewData>('/api/overview');

  return (
    <>
      <PageHeader
        title="Visão geral"
        description="Quem precisa de atendimento, quem pode retornar, o que está agendado e quais etapas de retorno estão programadas."
        actions={
          <Link className="button button-primary" to="/servicos/novo">
            Registrar serviço
          </Link>
        }
      />

      <ErrorBox error={error} />

      {store && !store.storeInfoConfirmed ? (
        <Alert tone="warning" title="Dados da loja ainda não confirmados">
          Endereço, telefone e horários estão em rascunho e não devem ser usados em mensagem
          pública. Confirme em <Link to="/configuracoes/loja">Configurações › Loja e marca</Link>.
        </Alert>
      ) : null}

      {data?.notifications.length ? (
        <Alert tone={data.notifications.some((n) => n.severity === 'critical') ? 'error' : 'warning'} title="Avisos">
          <ul style={{ margin: '4px 0 0 18px' }}>
            {data.notifications.slice(0, 5).map((n) => (
              <li key={n.id}>
                <strong>{n.title}</strong> {n.body ? `— ${n.body}` : ''}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <div className="indicadores grid cols-4" style={{ marginBottom: 16 }}>
        {(data?.indicators ?? []).map((indicator) => (
          <div className="indicator" key={indicator.key}>
            <div className="value">{indicator.value}</div>
            <div className="label">{indicator.label}</div>
            <div className="note">{indicator.note}</div>
          </div>
        ))}
      </div>

      <div className="grid cols-2">
        <Card title="Quem precisa de atendimento" tight>
          <Lista
            items={data?.needsHumanAttention ?? []}
            empty="Ninguém aguardando atendimento humano."
          />
        </Card>

        <Card title="Quem pode retornar" description="Próximos 30 dias" tight>
          <Lista
            items={data?.canReturn ?? []}
            empty="Nenhum retorno nos próximos 30 dias. Os retornos mais distantes aparecem em Lembretes e retornos."
          />
        </Card>

        <Card title="Serviços agendados" description="Próximos 7 dias" tight>
          <Lista items={data?.scheduledServices ?? []} empty="Nenhum serviço agendado nos próximos 7 dias." />
        </Card>

        <Card title="Etapas de retorno programadas" tight>
          <Lista
            items={data?.scheduledReminderSteps ?? []}
            empty="Nenhuma etapa de retorno na fila."
          />
        </Card>
      </div>
    </>
  );
}
